import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { OpSelect } from 'new-components/DLS/OpSelect/OpSelect';
import { OpForm, OnSubmitArgs } from 'new-components/DLS/OpForm/OpForm';
import { OpFormItem } from 'new-components/DLS/OpForm/formComponents/OpFormItem/OpFormItem';
import { OpModal } from 'new-components/DLS/OpModal/OpModal';
import { notificationService } from 'containers/AlertWrapper/notificationService';
import { useSelectorJs } from 'utils/customHooks';
import { useIdentityAndUser } from 'utils/customHooks/useIdentityAndUser';
import { getOpParams } from 'utils/getOpParams';
import { heliumSend } from 'utils/helpers';

import { SIGNED_OUT_VISITOR_ID } from '../../../../constants/userActivities';
import { DATE_TIME_FORMAT } from '../../../../constants/dates';
import { fetchVisits, VisitsState } from '../../../../store/slices/visitsSlice';
import {
  searchVisitors,
  searchVisitorsToday,
  VisitorSearchState,
} from '../../../../store/slices/visitorSearchSlice';
import { AppDispatch, ImmutableRootState } from '../../../../store/store';
import { Visitor } from '../../../../types/visitTypes';
import { formatFullName } from '../../../../utils/utils';

interface ReturnItemsValues {
  confirmReturnItems: number;
}

interface ReturnItemsModalProps {
  open: boolean;
  checkedVisitors: Visitor[];
  onClose: () => void;
  updateVisitorStatus: (visitorId: number) => void;
  setLoading: (loading: boolean) => void;
}

const ReturnItemsModal = ({
  open,
  checkedVisitors,
  onClose,
  updateVisitorStatus,
  setLoading,
}: ReturnItemsModalProps) => {
  const { t } = useTranslation();

  const dispatch: AppDispatch = useDispatch();

  const { orgId } = getOpParams();
  const { userId: globalUserId } = useIdentityAndUser();

  const globalLocationId = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'globalLocation', 'id']),
  ) as number;
  const selectedVisit = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visits', 'selectedVisit']),
  ) as VisitsState['selectedVisit'];
  const lastSearchParams = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visitorSearch', 'lastSearchParams']),
  ) as VisitorSearchState['lastSearchParams'];

  const [form] = OpForm.useForm();
  const [submitting, setSubmitting] = useState<boolean>(false);

  const handleOk = async () => {
    form
      .validateFields()
      .then(() => {
        setSubmitting(true);
        form.submit();
      })
      .catch(() => {
        notificationService.show('error', t('notification.validationError'));
      });
  };

  const handleSubmit = useCallback(
    async ({ values }: OnSubmitArgs<ReturnItemsValues>) => {
      try {
        // update status of visit and visitors
        for (const visitor of checkedVisitors) {
          if (visitor.id) {
            updateVisitorStatus(visitor.id);
          }
        }

        await heliumSend('newVisitorExit', [orgId, selectedVisit?.id], {
          signOut: dayjs().utc().format(DATE_TIME_FORMAT),
          confirmReturnItems: values.confirmReturnItems,
        });

        notificationService.show(
          'success',
          t('visitors.returnItemsModal.notification.signOutSuccess'),
        );

        // log activity
        const fullNameList = checkedVisitors
          ?.map((visitor: Visitor) =>
            formatFullName(
              visitor.firstName,
              visitor.middleName,
              visitor.lastName,
            ),
          )
          .join(', ');
        if (!globalUserId) {
          notificationService.show(
            'warning',
            t('notification.activityLogMasterModeWarning'),
          );
        } else {
          await heliumSend('newActivityLog', [orgId], {
            userId: globalUserId,
            activityId: SIGNED_OUT_VISITOR_ID,
            details: fullNameList,
          });
        }
        dispatch(fetchVisits({ orgId }));
        if (Object.keys(lastSearchParams).length !== 0) {
          dispatch(
            searchVisitors({
              ...lastSearchParams,
              orgId,
              locationId: lastSearchParams.locationId || globalLocationId!,
            }),
          );
        }

        dispatch(searchVisitorsToday({ orgId }));

        form.resetFields();
        onClose();
      } catch {
        // Failed to sign out
      } finally {
        setSubmitting(false);
        setLoading(false);
      }
    },
    [
      orgId,
      selectedVisit,
      checkedVisitors,
      t,
      dispatch,
      form,
      globalUserId,
      globalLocationId,
      lastSearchParams,
      updateVisitorStatus,
      onClose,
      setLoading,
    ],
  );

  return (
    <OpModal
      open={open}
      title={t('visitors.returnItemsModal.title')}
      onOk={handleOk}
      onCancel={onClose}
      centered
      width={720}
      confirmLoading={submitting}
    >
      <OpForm
        form={form}
        onSubmit={handleSubmit}
        defaultButtons={false}
        hasError={false}
        initialValues={{ confirmReturnItems: 1 }}
      >
        <OpFormItem
          label={t('visitors.returnItemsModal.label')}
          name="confirmReturnItems"
          style={{ width: '100%' }}
          rules={[
            {
              required: true,
              message: t('visitors.returnItemsModal.required'),
            },
          ]}
        >
          <OpSelect
            placeholder={t('visitors.returnItemsModal.placeholder')}
            options={[
              { label: t('visitors.returnItemsModal.yes'), value: 1 },
              { label: t('visitors.returnItemsModal.no'), value: 0 },
            ]}
          />
        </OpFormItem>
      </OpForm>
    </OpModal>
  );
};

export default ReturnItemsModal;
